// Quy tắc dùng chung cho "Số ngày hẹn trả" và "Hẹn trả máy" của phiếu dịch vụ:
// - Số ngày hẹn trả bắt buộc nhập và phải lớn hơn hoặc bằng 0
// - Hẹn trả máy bắt buộc chọn và không được nhỏ hơn ngày mốc (trả ngay trong ngày = 0 ngày)
//
// Ngày mốc khác nhau theo từng màn:
// - Màn nhận máy (CreateServiceRepairForm): mốc là ngày hiện tại
// - Màn chỉnh sửa (UpdateServiceRepairForm): mốc là createDate của phiếu, vì "Số ngày hẹn trả"
//   lưu trong DB được tính từ ngày tạo phiếu chứ không phải ngày mở màn sửa.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Mốc 00:00 của một ngày – so sánh theo ngày, không phụ thuộc giờ trong ngày
export const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

export const toDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

// Ngày mốc: nhận vào bất kỳ kiểu nào (string từ API, Date, null), không hợp lệ thì lấy hôm nay
export const resolveBaseDate = (base?: unknown): Date =>
  toDate(base) ?? new Date();

// Số ngày lệch giữa ngày hẹn trả và ngày mốc (cùng ngày = 0, hôm sau = 1...)
export const diffDaysFromBase = (target: Date, base?: unknown) =>
  Math.round(
    (startOfDay(target).getTime() -
      startOfDay(resolveBaseDate(base)).getTime()) /
      MS_PER_DAY,
  );

// Ngày hẹn trả tương ứng với số ngày hẹn trả, tính từ ngày mốc
export const dateFromDays = (days: number, base?: unknown) => {
  const d = startOfDay(resolveBaseDate(base));
  d.setDate(d.getDate() + days);
  return d;
};

// Ngày hẹn trả sớm nhất được phép chọn: chính ngày mốc (tương ứng 0 ngày)
export const minScheduleDate = (base?: unknown) => dateFromDays(0, base);

// Dùng để khoá nút lưu và chặn lúc submit: ô còn trống cũng tính là chưa hợp lệ
export const isDaysReturnInvalid = (daysReturn: unknown) => {
  const days = toNumber(daysReturn);
  return days === null || days < 0;
};

export const isScheduleTimeInvalid = (scheduleTime: unknown, base?: unknown) => {
  const date = toDate(scheduleTime);
  return date === null || diffDaysFromBase(date, base) < 0;
};

// Dùng cho prop `error` của input: chỉ bôi đỏ khi đã nhập nhưng nhập sai,
// ô còn trống thì để dấu * và chặn lúc lưu
export const hasDaysReturnError = (daysReturn: unknown) => {
  const days = toNumber(daysReturn);
  return days !== null && days < 0;
};

export const hasScheduleTimeError = (scheduleTime: unknown, base?: unknown) => {
  const date = toDate(scheduleTime);
  return date !== null && diffDaysFromBase(date, base) < 0;
};

const formatDate = (d: Date) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(
    2,
    "0",
  )}/${d.getFullYear()}`;

export const DAYS_RETURN_INVALID_MESSAGE =
  "Vui lòng nhập Số ngày hẹn trả (lớn hơn hoặc bằng 0) cho tất cả sản phẩm!";

// Không truyền mốc thì hiểu là so với ngày hiện tại (màn nhận máy),
// có mốc thì nói rõ ngày tạo phiếu để người dùng biết đang so với ngày nào
export const scheduleTimeInvalidMessage = (base?: unknown) =>
  toDate(base) === null
    ? "Vui lòng chọn Ngày hẹn trả không nhỏ hơn ngày hiện tại cho tất cả sản phẩm!"
    : `Vui lòng chọn Ngày hẹn trả không nhỏ hơn ngày tạo phiếu (${formatDate(
        resolveBaseDate(base),
      )}) cho tất cả sản phẩm!`;
